import { debounceTime, distinctUntilChanged, filter, interval, map, take } from "rxjs";
import { FunctionUtilities } from "./function-utilities";

export class ElementUtilities {

    public static isDetached(element: HTMLElement | null): boolean {
        while (element) {
            if (element === document.documentElement) {
                return false; // Element is still attached if we reach the document element
            }

            element = element.parentElement;
        }

        return true;
    }

    public static getSiblings(element: HTMLElement) {
        if (element.parentElement) {
            return Array.from(element.parentElement.children).filter((child) => child !== element);
        } else {
            return [];
        }
    }

    public static hasSiblingsOnTheSameRow(element: HTMLElement) {
        const boundingClientRect = element.getBoundingClientRect();
        const siblings = this.getSiblings(element);
        return siblings.some((sibling) => {
            const siblingClientRect = sibling.getBoundingClientRect();
            return this.isVisible(sibling) &&
                siblingClientRect.top >= boundingClientRect.top &&
                siblingClientRect.top < boundingClientRect.bottom;
        });
    }

    public static isVisible(element: Element) {
        const boundingClientRect = element.getBoundingClientRect();
        return boundingClientRect.width > 0 && boundingClientRect.height > 0;
    }

    public static isElementTopLeftMost(element: Element) {
        const boundingClientRect = element.getBoundingClientRect();
        const siblings = this.getSiblings(element as HTMLElement);
        const hasSiblingToTheLeftOrTop = siblings.some((sibling) => {
            const siblingClientRect = sibling.getBoundingClientRect();
            return this.isVisible(sibling) &&
                (siblingClientRect.top < boundingClientRect.top || siblingClientRect.left < boundingClientRect.left);
        });
        return !hasSiblingToTheLeftOrTop;
    }

    public static isElementInView(element: Element, parentContainer?: Element) {
        if (!this.isVisible(element)) {
            return false;
        }

        const boundingClientRect = element.getBoundingClientRect();
        const elementTop = boundingClientRect.top;
        const elementBottom = elementTop + boundingClientRect.bottom;
        if (parentContainer) {
            const parentRect = parentContainer.getBoundingClientRect();
            return (elementTop >= parentRect.top && elementTop <= parentRect.bottom) ||
                (elementBottom >= parentRect.top && elementBottom <= parentRect.bottom);
        }

        return (elementTop >= 0 && elementTop <= window.innerHeight) ||
            (elementBottom >= 0 && elementBottom <= window.innerHeight);
    }

    public static stringToElements(content: string) {
        return Array.from(new DOMParser().parseFromString(content, "text/html").body.children) as HTMLElement[];
    }

    public static removeAllChildrenWithSelector(element: HTMLElement, selector: string) {
        if (FunctionUtilities.isFunction(element.querySelectorAll)) {
            // sometimes text node get passed in as HTMLElement but they won't have the function
            const elements = element.querySelectorAll(selector);
            elements.forEach((removeElement) => element.removeChild(removeElement));
        }
        return element;
    }

    public static removeClassFromElement(element: HTMLElement, className: string) {
        if (element.classList) {
            if (element.classList.contains(className)) {
                element.classList.remove(className);
            }
        }
        return element;
    }

    public static isElementVerticallyWithinContainerBounds(container: DOMRect, element: DOMRect) {
        return element.bottom <= container.bottom
            && container.top <= element.top;
    }

    public static waitUntilDescendantVisible(element: HTMLElement, selector: string, intervalMs = 20 /* ~50fps */) {
        return interval(intervalMs).pipe(
            map(() => element.querySelector(selector) as HTMLElement),
            filter((selectedElement) => selectedElement && this.isVisible(selectedElement)),
            take(1),
        );
    }

    public static waitUntilVisible(element: HTMLElement, intervalMs = 20 /* ~50fps */) {
        return interval(intervalMs).pipe(
            filter(() => this.isVisible(element)),
            take(1) // Take the first stable position
        );
    }

    public static waitUntilPositionStable(element: HTMLElement, debounceTimeMs = 50, intervalMs = 20 /* ~50fps */) {
        return interval(intervalMs).pipe(
            map(() => element.getBoundingClientRect()),
            distinctUntilChanged((prev, curr) => prev.top === curr.top),
            debounceTime(debounceTimeMs), // Wait after position stops changing
            take(1) // Take the first stable position
        );
    }

    public static onElementSizeChanged(element: HTMLElement, callback: (element: HTMLElement) => void) {
        // Observe height changes of frElement
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                callback(entry.target as HTMLElement);
            }
        });
        resizeObserver.observe(element);

        return resizeObserver;
    }
}
